import { translate } from 'support/translate';
import { AppData, Colors, Lightning } from '@lightningjs/sdk';
import ActionsList from 'components/common/ActionsList';
import { constants } from 'aliases';
import { getFontFaceFromStyle } from 'support/textUtils';
import Button from 'components/common/buttons/Button';
import { getImageTextureObj } from 'support/generalUtils';
import TextLabel from 'components/common/TextLabel';

export type RemoveConfirmationModalArgs = Partial<
  Pick<
    RemoveConfirmationModalTemplateSpec,
    'numberOfItems' | 'detailsTitle' | 'onRemoveItems' | 'onDismissModal'
  >
>;
export interface RemoveConfirmationModalTemplateSpec
  extends Lightning.Component.TemplateSpec {
  numberOfItems: number;
  detailsTitle: string;
  onRemoveItems: (() => void) | null;
  onDismissModal: (() => void) | null;

  BackgroundOverlay: Lightning.Component;
  Modal: {
    Icon: Lightning.Texture;
    Content: {
      Title: typeof TextLabel;
      Description: typeof TextLabel;
    };
    ButtonList: typeof ActionsList;
  };
}

const MODAL_X = 0;
const MODAL_Y = 0;
const MODAL_WIDTH = 1920;
const MODAL_HEIGHT = 1080;
const DEFAULT_PADDING = 246;

const CONTENT_MAX_WIDTH = 1118;
const DEFAULT_CONTENT_MARGIN = 90;

const TITLE_FONT_SIZE = 64;
const TITLE_LINE_HEIGHT = 77;
const DESCRIPTION_FONT_SIZE = 44;
const DESCRIPTION_LINE_HEIGHT = 53;

const ICON_SIZE = 130;

const BUTTON_HEIGHT = 70;
const BUTTON_FONT_SIZE = 29;
const BUTTON_SPACING = 32;
const BUTTON_MARGIN = 100;

const ICON = getImageTextureObj(
  'static/images/trash-can-remove.svg',
  ICON_SIZE,
  ICON_SIZE,
);

export default class RemoveConfirmationModal
  extends Lightning.Component<RemoveConfirmationModalTemplateSpec>
  implements
    Lightning.Component
      .ImplementTemplateSpec<RemoveConfirmationModalTemplateSpec>
{
  protected _numberOfItems = 0;
  protected _detailsTitle = '';
  protected _onRemoveItems: RemoveConfirmationModalTemplateSpec['onRemoveItems'] =
    null;
  protected _onDismissModal: RemoveConfirmationModalTemplateSpec['onDismissModal'] =
    null;

  private _Modal = this.getByRef('Modal')!;
  private _Content = this._Modal.getByRef('Content')!;
  private _Title = this._Content.getByRef('Title')!;
  private _Description = this._Content.getByRef('Description')!;
  private _ButtonList = this._Modal.getByRef('ButtonList')!;

  public update(props: RemoveConfirmationModalArgs) {
    const {
      numberOfItems = this.numberOfItems,
      detailsTitle = this.detailsTitle,
      onRemoveItems = this.onRemoveItems,
      onDismissModal = this.onDismissModal,
    } = props;

    this.numberOfItems = numberOfItems;
    this.detailsTitle = detailsTitle;
    this.onRemoveItems = onRemoveItems;
    this.onDismissModal = onDismissModal;
    this.updateDescriptionLabel();
  }

  get title() {
    return [this._Title.text?.text ?? '', this._Description.text?.text ?? ''];
  }

  set numberOfItems(numberofItems: number) {
    this._numberOfItems = numberofItems;

    this.updateDescriptionLabel();
  }

  get numberOfItems() {
    return this._numberOfItems;
  }

  set detailsTitle(title: string) {
    this._detailsTitle = title;
  }

  get detailsTitle() {
    return this._detailsTitle;
  }

  get onRemoveItems() {
    return this._onRemoveItems;
  }

  set onRemoveItems(
    onRemoveItems: RemoveConfirmationModalTemplateSpec['onRemoveItems'],
  ) {
    this._onRemoveItems = onRemoveItems;
  }

  get onDismissModal() {
    return this._onDismissModal;
  }

  set onDismissModal(
    onDismissModal: RemoveConfirmationModalTemplateSpec['onDismissModal'],
  ) {
    this._onDismissModal = onDismissModal;
  }

  static override _template(): Lightning.Component.Template<RemoveConfirmationModalTemplateSpec> {
    return {
      alpha: 0,
      BackgroundOverlay: {
        rect: true,
        w: 1920,
        h: 1080,
        color: Colors('modalBackground').get(),
        alpha: 0.95,
        zIndex: constants.ui.zIndex.modalLayer,
      },
      Modal: {
        flex: {
          direction: 'column',
          alignItems: 'center',
        },
        x: MODAL_X,
        y: MODAL_Y,
        w: MODAL_WIDTH,
        h: MODAL_HEIGHT,
        zIndex: constants.ui.zIndex.modalLayer + 1,
        Icon: {
          flexItem: {
            marginTop: DEFAULT_PADDING,
          },
          ...ICON,
        },
        Content: {
          flex: {
            direction: 'column',
            alignItems: 'center',
          },
          flexItem: {
            marginTop: DEFAULT_CONTENT_MARGIN,
            marginBottom: DEFAULT_CONTENT_MARGIN,
          },
          Title: {
            type: TextLabel,
            w: CONTENT_MAX_WIDTH,
            text: {
              fontSize: TITLE_FONT_SIZE,
              fontFace: getFontFaceFromStyle('medium'),
              textColor: Colors('text').get(),
              lineHeight: TITLE_LINE_HEIGHT,
              textAlign: 'center',
              text: translate('myStuff.removeConfirmationModal.title'),
            },
          },
          Description: {
            type: TextLabel,
            w: CONTENT_MAX_WIDTH,
            text: {
              fontSize: DESCRIPTION_FONT_SIZE,
              fontFace: getFontFaceFromStyle('light'),
              textColor: Colors('text').get(),
              lineHeight: DESCRIPTION_LINE_HEIGHT,
              textAlign: 'center',
            },
          },
        },
        ButtonList: {
          type: ActionsList,
          flex: {
            direction: 'row',
          },
          spacing: BUTTON_SPACING,
          signals: {
            $dismissModal: true,
            $removeItems: true,
          },
          zIndex: constants.ui.zIndex.modalLayer + 1,
          items: [
            {
              type: Button,
              height: BUTTON_HEIGHT,
              margin: BUTTON_MARGIN,
              fontSize: BUTTON_FONT_SIZE,
              zIndex: constants.ui.zIndex.modalLayer + 2,
              label: translate('global.cancelButton'),
              fontFace: getFontFaceFromStyle('medium'),
              action: '$dismissModal',
              signals: { $onHover: '$onHover' },
              passSignals: { $dismissModal: true },
            },
            {
              type: Button,
              height: BUTTON_HEIGHT,
              margin: BUTTON_MARGIN,
              fontSize: BUTTON_FONT_SIZE,
              zIndex: constants.ui.zIndex.modalLayer + 2,
              label: translate(
                'myStuff.removeConfirmationModal.confirmationButton',
              ),
              fontFace: getFontFaceFromStyle('medium'),
              action: '$removeItems',
              signals: { $onHover: '$onHover' },
              passSignals: { $removeItems: true },
            },
          ],
        },
      },
    };
  }

  override _setup() {
    super._setup();
  }

  override _active() {
    this.updateDescriptionLabel();
  }

  override _getFocused() {
    return this._ButtonList;
  }

  private updateDescriptionLabel() {
    this._Description.patch({
      text: {
        text: translate(
          'myStuff.removeConfirmationModal.description',
          this._numberOfItems,
          this._detailsTitle,
        ),
      },
    });
  }

  hide() {
    this.patch({ alpha: 0 });
  }

  show() {
    this.patch({ alpha: 1 });
  }

  override _handleBack() {
    this.$dismissModal();
  }

  override _handleKey() {
    // Do not allow the user to bypass the app launch screen
    return true;
  }

  private $removeItems() {
    AppData?.modalService.close();
    this.onRemoveItems?.();
  }

  private $dismissModal() {
    AppData?.modalService.close();
    this.onDismissModal?.();
  }
}
